/**
 * Admin login API (`/api/v1/admin/auth/*`).
 */

import { getAdminJwt } from "./auth-storage.js";

const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ??
  "/api/v1";

/**
 * Error carrying the HTTP status so callers can distinguish a genuine auth
 * failure (401 → sign out) from a transient one (429 rate-limited, 5xx, network)
 * that must NOT destroy the local session.
 */
export class AuthApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "AuthApiError";
    this.status = status;
  }
}

export type AdminRoleDto = "platform_admin" | "tenant_admin" | "tenant_operator";

export type AdminThemePreferenceDto = "light" | "dark" | "system";

export interface AdminUserDto {
  id: number;
  email: string;
  role: AdminRoleDto;
  tenantId: number | null;
  tenantExternalId: string | null;
  /** Effective governance permission keys (SEC-03 / custom roles). */
  permissions?: string[];
  /** SEC-09: tenant requires MFA but operator must finish enrollment in Settings. */
  mfaEnrollmentRequired?: boolean;
  /** P2-UX-14: persisted dashboard theme. */
  themePreference?: AdminThemePreferenceDto;
}

/** Password step completed; either MFA is required or session JWT is issued. */
export type LoginAdminResult =
  | { mfaRequired: true; mfaToken: string; expiresInSeconds: number }
  | {
      mfaEnrollmentRequired: true;
      token: string;
      user: AdminUserDto;
      expiresInSeconds: number;
    }
  | { mfaRequired: false; token: string; user: AdminUserDto };

export async function loginAdmin(email: string, password: string): Promise<LoginAdminResult> {
  const res = await fetch(`${API_BASE}/admin/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const err = data as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `Login failed: ${res.status}`);
  }
  if (data.mfaRequired === true && typeof data.mfaToken === "string") {
    return {
      mfaRequired: true,
      mfaToken: data.mfaToken,
      expiresInSeconds:
        typeof data.expiresInSeconds === "number" ? data.expiresInSeconds : 300,
    };
  }
  if (
    data.mfaEnrollmentRequired === true &&
    typeof data.token === "string" &&
    data.user &&
    typeof data.user === "object"
  ) {
    return {
      mfaEnrollmentRequired: true,
      token: data.token,
      user: data.user as AdminUserDto,
      expiresInSeconds:
        typeof data.expiresInSeconds === "number" ? data.expiresInSeconds : 8 * 60 * 60,
    };
  }
  if (typeof data.token === "string" && data.user && typeof data.user === "object") {
    return {
      mfaRequired: false,
      token: data.token,
      user: data.user as AdminUserDto,
    };
  }
  throw new Error("Unexpected login response from server.");
}

export async function verifyAdminMfaLogin(
  mfaToken: string,
  code: string
): Promise<{ token: string; user: AdminUserDto }> {
  const res = await fetch(`${API_BASE}/admin/auth/mfa/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mfaToken, code }),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    const err = data as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `MFA verification failed: ${res.status}`);
  }
  if (typeof data.token === "string" && data.user && typeof data.user === "object") {
    return { token: data.token, user: data.user as AdminUserDto };
  }
  throw new Error("Unexpected MFA response from server.");
}

export async function fetchMfaStatus(): Promise<{ mfaEnabled: boolean }> {
  const token = getAdminJwt();
  const res = await fetch(`${API_BASE}/admin/auth/mfa/status`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    throw new Error(`MFA status failed: ${res.status}`);
  }
  return res.json() as Promise<{ mfaEnabled: boolean }>;
}

export async function startMfaSetup(): Promise<{ otpauthUrl: string; recoveryCodes: string[] }> {
  const token = getAdminJwt();
  const res = await fetch(`${API_BASE}/admin/auth/mfa/setup`, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    throw new Error(`MFA setup failed: ${res.status}`);
  }
  return res.json() as Promise<{ otpauthUrl: string; recoveryCodes: string[] }>;
}

export async function confirmMfaEnable(code: string): Promise<void> {
  const token = getAdminJwt();
  const res = await fetch(`${API_BASE}/admin/auth/mfa/enable`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `MFA enable failed: ${res.status}`);
  }
  // API returns 204 No Content on success.
}

export async function disableMfa(password: string): Promise<void> {
  const token = getAdminJwt();
  const res = await fetch(`${API_BASE}/admin/auth/mfa/disable`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `MFA disable failed: ${res.status}`);
  }
}

export async function fetchAdminMe(): Promise<AdminUserDto> {
  const token = getAdminJwt();
  const res = await fetch(`${API_BASE}/admin/auth/me`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    throw new AuthApiError(`me failed: ${res.status}`, res.status);
  }
  return res.json() as Promise<AdminUserDto>;
}

export async function patchAdminPreferences(body: {
  themePreference: AdminThemePreferenceDto;
}): Promise<void> {
  const token = getAdminJwt();
  const res = await fetch(`${API_BASE}/admin/auth/me/preferences`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(err.message ?? err.error ?? `preferences failed: ${res.status}`);
  }
}

export async function logoutAdmin(): Promise<void> {
  const token = getAdminJwt();
  await fetch(`${API_BASE}/admin/auth/logout`, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  }).catch(() => undefined);
}

/**
 * First organization admin: exchange bootstrap token (from tenant creation) for a session JWT.
 */
export async function bootstrapTenantAdmin(body: {
  tenantExternalId: string;
  email: string;
  password: string;
  bootstrapToken: string;
}): Promise<{ token: string; user: AdminUserDto }> {
  const res = await fetch(`${API_BASE}/admin/auth/bootstrap-tenant-admin`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as {
      message?: string;
      error?: string;
    };
    throw new Error(err.message ?? err.error ?? `Bootstrap failed: ${res.status}`);
  }
  return res.json() as Promise<{ token: string; user: AdminUserDto }>;
}
